import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popper,
  PopperProps,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AutocompleteNumbers } from 'components/atoms/Autocompletes/AutocompleteNumbers';
import AutomaticConditionGroup from 'components/atoms/ConditionBuilder/AutomaticConditionGroup';
import { AutomaticCondition, ConditionType } from 'interfaces/automaticCondition';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import PostProcessingActionInterface, {
  Input,
  InputSource,
  InputType,
  MappingFilterMetadata,
  PostProcessingActionCreated,
} from 'interfaces/postProcessingAction';
import { QCImpactId } from 'interfaces/qcMarkers';
import { StainType } from 'interfaces/stainType';
import { Taxonomy } from 'interfaces/taxonomy';
import {
  cloneDeep,
  concat,
  Dictionary,
  difference,
  filter,
  find,
  first,
  forEach,
  get,
  groupBy,
  includes,
  isArray,
  isEmpty,
  isNil,
  isNumber,
  isString,
  join,
  map,
  pullAt,
  set,
  size,
  slice,
  some,
  sortBy,
  toString,
  uniq,
  values,
} from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { DEPENDENT_ON_INPUT_TYPE, NEIGHBOR_STAINS_INPUT_KEY, TARGET_STAINS_INPUT_KEY } from '../consts';
import { HighlightedPresetInput, PresetActionAccess, PresetInputSection } from '../Presets/presetUtils';
import {
  addBulkSelectionValues,
  BulkSelectionExclusion,
  getBulkSelectionShortcuts,
} from '../Presets/smartPresetSelections';
import {
  getInstanceCountThresholdOptions,
  INSTANCE_COUNT_POSITIVITY_ACTION_NAME,
  INSTANCE_COUNT_THRESHOLDS_INPUT_KEY,
  shouldUpdateInstanceCountThresholds,
  syncInstanceCountThresholds,
} from './instanceCountPositivity';
import { InstanceCountPositivityThresholds } from './InstanceCountPositivityThresholds';
import {
  checkOptionExist,
  getConditionValue,
  getInputType,
  getIsRequired,
  hasNonEmptyNewOptionValues,
  isFeatureBetweenStains,
  validateInput,
} from './utils';

interface PostProcessingActionProps {
  id: string;
  index: number;
  action: PostProcessingActionCreated;
  setAction: (action: PostProcessingActionCreated, index: number) => void;
  removeAction: (index: number) => void;
  moveActionUp: () => void;
  moveActionDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  postProcessingActionsById: Dictionary<PostProcessingActionInterface>;
  getOptions: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => any[];
  getOptionsByValue: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => Dictionary<{ id: string | number; label: string }>;
  getMappingFiltersMetadataForLogicalQuery: (
    stain: string,
    actionInput: string,
    actionAttributesTable: AttributesTable,
    index: number
  ) => MappingFilterMetadata[];
  // for now this is only relevant for vmplex features in neighbor_stains input- therefore we use it only in the multiselect input
  getAdditionalInputError?: (inputValue: any, input: Input, action: PostProcessingActionCreated) => string;
  presetInputSection: PresetInputSection;
  highlightedInputs: HighlightedPresetInput[];
  stainTypes: StainType[];
  taxonomies: Taxonomy[];
  qcMarkersSettings?: Record<string, string>;
  presetAuthoring?: {
    access: PresetActionAccess;
    onAccessChange: (access: PresetActionAccess) => void;
  };
}

const presetActionAccessOptions: Array<{
  value: PresetActionAccess;
  label: string;
}> = [
  { value: 'editable', label: 'Editable' },
  { value: 'locked', label: 'Hidden and locked' },
  { value: 'editable_advanced', label: 'Advanced users only' },
];

const presetActionAccessDescriptions: Record<PresetActionAccess, string> = {
  editable: 'Shown to every preset user and editable.',
  locked: 'Hidden from preset users and always applied exactly as saved.',
  editable_advanced: 'Shown and editable only to users with advanced access.',
};

export const PostProcessingAction: React.FC<React.PropsWithChildren<PostProcessingActionProps>> = ({
  id,
  index,
  postProcessingActionsById,
  action,
  setAction,
  removeAction,
  moveActionUp,
  moveActionDown,
  isFirst,
  isLast,
  getOptions,
  getOptionsByValue,
  getMappingFiltersMetadataForLogicalQuery,
  getAdditionalInputError,
  presetInputSection,
  highlightedInputs,
  stainTypes,
  taxonomies,
  qcMarkersSettings = {},
  presetAuthoring,
}) => {
  const [expandAccordion, setExpandAccordion] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const lastAutoAppliedInstanceCountThresholdsRef = useRef<Record<string, unknown>>({});
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };
  const [displayAdvancedInputs, setDisplayAdvancedInputs] = useState(false);
  const [bulkSelectionExclusionsByInputKey, setBulkSelectionExclusionsByInputKey] = useState<
    Record<string, BulkSelectionExclusion[]>
  >({});

  const getHighlightedInput = (input: Input) =>
    find(highlightedInputs, {
      section: presetInputSection,
      actionInstanceId: action.id,
      inputKey: input.inputKey,
    });

  useEffect(() => {
    const actionDefinition = postProcessingActionsById[action.actionId];
    if (!actionDefinition) {
      return;
    }

    const nextAction = cloneDeep(action);
    let hasAutomaticSelection = false;

    forEach(actionDefinition.inputs, (input) => {
      const inputType = getInputType(input, nextAction);
      if (
        input.parentInputKey ||
        !includes([InputType.SELECT, InputType.MULTISELECT], inputType) ||
        (input.displayCondition && !getConditionValue(input.displayCondition, nextAction))
      ) {
        return;
      }

      const currentValue = get(nextAction, input.inputKey);
      const hasCurrentValue =
        inputType === InputType.MULTISELECT ? !isEmpty(currentValue) : !isNil(currentValue) && currentValue !== '';
      if (hasCurrentValue) {
        return;
      }

      const selectedStainsForInput = getRelevantSelectedStainsForInput(input, nextAction);
      const options = getOptions(
        input.inputSource,
        selectedStainsForInput,
        index,
        nextAction.actionInput,
        get(nextAction, input.inputSourceDependentOn)
      );

      // Instance H-Score's binned columns default to every currently available probe/compartment/
      // dilation combination selected, so a newly configured probe is included without manual action.
      if (
        inputType === InputType.MULTISELECT &&
        nextAction.actionName === 'instance_h_score' &&
        input.inputSource === InputSource.CELL_BINS &&
        !isEmpty(options)
      ) {
        set(nextAction, input.inputKey, map(options, 'id'));
        hasAutomaticSelection = true;
        return;
      }

      const preferredOption =
        nextAction.actionName === 'instance_h_score' &&
        input.inputSource === InputSource.INTERMEDIATE_AREAS_PER_TARGET_STAIN
          ? find(options, { id: 'tissue' })
          : undefined;
      const selectedOption = size(options) === 1 ? first(options) : preferredOption;
      if (isNil(selectedOption?.id)) {
        return;
      }

      set(nextAction, input.inputKey, inputType === InputType.MULTISELECT ? [selectedOption.id] : selectedOption.id);
      hasAutomaticSelection = true;

      if (inputType === InputType.SELECT) {
        forEach(
          filter(actionDefinition.inputs, (dependentInput) => dependentInput.inputSourceDependentOn === input.inputKey),
          (dependentInput) => {
            set(nextAction, dependentInput.inputKey, null);
            if (dependentInput.inputType === DEPENDENT_ON_INPUT_TYPE) {
              const dependentInputType = includes(values(InputType), selectedOption.input)
                ? selectedOption.input
                : InputType.SELECT;
              nextAction.dependentOnInputTypes = {
                ...nextAction.dependentOnInputTypes,
                [dependentInput.inputKey]: dependentInputType,
              };
            }
          }
        );
      }
    });

    if (nextAction.actionName === INSTANCE_COUNT_POSITIVITY_ACTION_NAME) {
      const selectedStains = nextAction.stain ? [nextAction.stain] : nextAction.stains ?? [];
      const thresholdOptions = getInstanceCountThresholdOptions(
        getOptions(InputSource.INSTANCE_COUNT_THRESHOLDS, selectedStains, index, nextAction.actionInput)
      );
      const currentThresholds = get(nextAction, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY, {});
      const { thresholds, autoManagedThresholds, changedInstanceNames } = syncInstanceCountThresholds(
        currentThresholds,
        lastAutoAppliedInstanceCountThresholdsRef.current,
        thresholdOptions
      );
      lastAutoAppliedInstanceCountThresholdsRef.current = autoManagedThresholds;
      if (shouldUpdateInstanceCountThresholds(nextAction, thresholds)) {
        set(nextAction, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY, thresholds);
        hasAutomaticSelection = true;
        if (!isEmpty(changedInstanceNames)) {
          enqueueSnackbar('Positivity thresholds updated to match the edited H-score bins.', { variant: 'info' });
        }
      }
    }

    if (hasAutomaticSelection) {
      setAction(nextAction, index);
    }
  }, [action, enqueueSnackbar, getOptions, index, postProcessingActionsById, setAction]);

  const parseToFloatInRanges = (value: string, inputRange: { min?: number; max?: number }) => {
    const floatNumber = parseFloat(value);
    const numberBetweenRange =
      floatNumber < inputRange?.min ? inputRange?.min : floatNumber > inputRange?.max ? inputRange?.max : floatNumber;
    return numberBetweenRange;
  };

  const mappingFiltersMetadataForLogicalQuery: { [attributesTableKey: string]: MappingFilterMetadata[] } = {};
  forEach(
    AttributesTable,
    (actionAttributesTable) =>
      (mappingFiltersMetadataForLogicalQuery[actionAttributesTable] = getMappingFiltersMetadataForLogicalQuery(
        action?.stain,
        action?.actionInput,
        actionAttributesTable,
        index
      ))
  );

  const inputByType: Record<
    string,
    (input: Input, value?: string | string[] | AutomaticCondition, indexInList?: number) => ReactElement
  > = {
    [InputType.SELECT]: (input, value: string, indexInList) => {
      const selectedStainsForInput = getRelevantSelectedStainsForInput(input, action);
      const options = getOptions(
        input.inputSource,
        selectedStainsForInput,
        index,
        action.actionInput,
        get(action, input.inputSourceDependentOn)
      );
      const isError = !validateInput(get(action, input.inputKey), input, action, options);
      const isRequired = getIsRequired(input.required, action);
      const displayCondition = !input.displayCondition || getConditionValue(input.displayCondition, action);

      return (
        displayCondition && (
          <FormControl fullWidth variant="standard" error={isError} className={isError ? 'error' : undefined}>
            <Autocomplete
              PopperComponent={AutocompletePopper}
              size="small"
              options={options}
              groupBy={(option) => option.groupBy}
              value={
                value
                  ? {
                      id: value,
                      label:
                        getOptionsByValue(
                          input.inputSource,
                          selectedStainsForInput,
                          index,
                          action.actionInput,
                          get(action, input.inputSourceDependentOn)
                        )[value]?.label ?? value,
                    }
                  : null
              }
              isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
              renderInput={(params) => (
                <TextField
                  error={(isRequired && isEmpty(value)) || isError}
                  required={isRequired}
                  {...params}
                  label={`Select ${input.inputLabel}`}
                  size="medium"
                />
              )}
              filterOptions={(autocompleteOptions, params) => {
                const { inputValue } = params;
                const filtered = createFilterOptions()(autocompleteOptions, params);
                const isExisting = some(autocompleteOptions, (option) => inputValue === option.label);
                if (inputValue !== '' && !isExisting && input.freeSolo) {
                  filtered.push({
                    id: inputValue,
                    label: inputValue,
                    freeSolo: true,
                  });
                }

                return filtered;
              }}
              onChange={(event, selected) => {
                let newAction = { ...action };
                if (input.parentInputKey && indexInList !== undefined) {
                  const newValue = selected?.id;
                  const updatedParentInputValue = getUpdatedParentObject(action, input, indexInList, newValue);
                  set(newAction, input.parentInputKey, updatedParentInputValue);

                  if (input?.freeSolo) {
                    if (selected?.freeSolo) {
                      // set newOptionValues in newAction by indexInList
                      const newOptionValues = get(newAction, 'newOptionValues', []);
                      newOptionValues[indexInList] = selected?.id;
                      set(newAction, 'newOptionValues', newOptionValues);
                    } else {
                      const newOptionValues = get(newAction, 'newOptionValues', []);
                      newOptionValues[indexInList] = null;
                      set(newAction, 'newOptionValues', newOptionValues);
                    }
                  }
                } else {
                  set(newAction, input.inputKey, selected?.id);

                  if (input?.freeSolo) {
                    if (selected?.freeSolo) {
                      set(newAction, 'newOptionValues', [selected?.id]);
                    } else {
                      set(newAction, 'newOptionValues', []);
                    }
                  }
                }

                if (input.inputKey === 'action_params.attribute_to_map') {
                  set(newAction, 'newOptionValueInput', selected?.input);
                }

                // check if another inputs is dependent on this input, if so, set it to null
                const dependentInputs = filter(
                  postProcessingActionsById[action.actionId]?.inputs,
                  (inputToFind) => inputToFind.inputSourceDependentOn === input.inputKey
                );
                if (!isEmpty(dependentInputs)) {
                  map(dependentInputs, (dependentInput) => {
                    set(newAction, dependentInput.inputKey, null);
                    if (dependentInput?.inputType === DEPENDENT_ON_INPUT_TYPE) {
                      const isValidInputType = includes(values(InputType), selected?.input);
                      const inputType = isValidInputType ? selected?.input : InputType.SELECT;

                      newAction.dependentOnInputTypes = {
                        ...newAction.dependentOnInputTypes,
                        [dependentInput.inputKey]: inputType,
                      };
                    }
                  });
                }

                setAction(newAction, index);
              }}
              freeSolo={input.freeSolo}
            />
            {!checkOptionExist(get(action, input.inputKey), input, options) && (
              <FormHelperText error id="class-does-not-exist-error-text">
                This option does not exist anymore
              </FormHelperText>
            )}
          </FormControl>
        )
      );
    },
    [InputType.MULTISELECT]: (input, value: string[], indexInList) => {
      const selectedStainsForInput = getRelevantSelectedStainsForInput(input, action);
      const options = getOptions(
        input.inputSource,
        selectedStainsForInput,
        index,
        action.actionInput,
        get(action, input.inputSourceDependentOn)
      );
      const isError = !validateInput(value, input, action, options);
      const isRequired = getIsRequired(input.required, action);
      const errorMessage = !checkOptionExist(value, input, options)
        ? `Some options don't exist anymore: ${join(difference(value, map(options, 'id')), ', ')}`
        : (getAdditionalInputError && getAdditionalInputError(value, input, action)) || '';

      const optionsByValue = getOptionsByValue(input.inputSource, selectedStainsForInput, index, action.actionInput);
      const highlightedInput = getHighlightedInput(input);
      const bulkSelectionShortcuts = highlightedInput
        ? getBulkSelectionShortcuts({
            inputSource: input.inputSource,
            options: map(options, (option) => ({
              ...option,
              ignored: qcMarkersSettings[toString(option.id)] === QCImpactId.EXCLUDE,
            })),
            stainTypes,
            taxonomies,
          })
        : [];
      const bulkSelectionExclusions = bulkSelectionExclusionsByInputKey[input.inputKey] || [];

      return (
        <FormControl fullWidth variant="standard" error={isError} className={isError ? 'error' : undefined}>
          {!isEmpty(bulkSelectionShortcuts) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {map(bulkSelectionShortcuts, (shortcut) => (
                <Button
                  key={shortcut.id}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const newValue = addBulkSelectionValues(
                      value || [],
                      shortcut.result.selectedIds,
                      input.limitMultipleTo
                    );
                    const newAction = cloneDeep(action);
                    if (input.parentInputKey && indexInList !== undefined) {
                      const updatedParentInputValue = getUpdatedParentObject(newAction, input, indexInList, newValue);
                      set(newAction, input.parentInputKey, updatedParentInputValue);
                    } else {
                      set(newAction, input.inputKey, newValue);
                    }
                    setBulkSelectionExclusionsByInputKey((previous) => ({
                      ...previous,
                      [input.inputKey]: shortcut.result.excluded,
                    }));
                    setAction(newAction, index);
                  }}
                >
                  {shortcut.label}
                </Button>
              ))}
            </Box>
          )}
          <Autocomplete
            PopperComponent={AutocompletePopper}
            multiple
            getOptionDisabled={input?.limitMultipleTo ? () => size(value) >= input.limitMultipleTo : undefined}
            disableCloseOnSelect
            limitTags={2}
            size="small"
            options={options}
            groupBy={(option) => option.groupBy}
            value={
              value
                ? map(value, (val) => {
                    return {
                      id: !isString(val) && !isNumber(val) ? toString(val) : val,
                      label: toString(optionsByValue?.[val]?.label ?? val),
                    };
                  })
                : []
            }
            isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
            renderInput={(params) => (
              <TextField
                error={(isRequired && isEmpty(value)) || isError || Boolean(errorMessage)}
                required={isRequired}
                {...params}
                label={`Select ${input.inputLabel}`}
                size="medium"
              />
            )}
            onChange={(event, selected) => {
              const newValue = sortBy(map(selected, 'id'));
              setBulkSelectionExclusionsByInputKey((previous) => ({
                ...previous,
                [input.inputKey]: [],
              }));
              if (input.parentInputKey && indexInList !== undefined) {
                const updatedParentInputValue = getUpdatedParentObject(action, input, indexInList, newValue);
                setAction(set(action, input.parentInputKey, updatedParentInputValue), index);
              } else {
                setAction(set(action, input.inputKey, newValue), index);
              }
            }}
          />
          {!isEmpty(bulkSelectionExclusions) && (
            <FormHelperText>
              Excluded {bulkSelectionExclusions.length} unavailable, QC-failed, ignored, or deleted value
              {bulkSelectionExclusions.length === 1 ? '' : 's'}.
            </FormHelperText>
          )}
          {errorMessage && (
            <FormHelperText error id="class-does-not-exist-error-text">
              {errorMessage}
            </FormHelperText>
          )}
        </FormControl>
      );
    },
    [InputType.NUMBER]: (input, value, indexInList) => {
      return (
        <TextField
          error={!validateInput(value, input, action)}
          required={getIsRequired(input.required, action)}
          value={value}
          id="outlined-number"
          label={input.inputLabel}
          type="number"
          inputProps={{
            max: input?.inputRange?.max,
            min: input?.inputRange?.min,
          }}
          onChange={(event) => {
            if (event.target.value === '') {
              if (input.parentInputKey && indexInList !== undefined) {
                const updatedParentInputValue = getUpdatedParentObject(action, input, indexInList, null);
                setAction(set(action, input.parentInputKey, updatedParentInputValue), index);
              } else {
                setAction(set(action, input.inputKey, null), index);
              }
            } else {
              const numberBetweenRange = parseToFloatInRanges(event.target.value, input?.inputRange);
              if (input.parentInputKey && indexInList !== undefined) {
                const updatedParentInputValue = getUpdatedParentObject(action, input, indexInList, numberBetweenRange);
                setAction(set(action, input.parentInputKey, updatedParentInputValue), index);
              } else {
                setAction(set(action, input.inputKey, numberBetweenRange), index);
              }
            }
          }}
        />
      );
    },
    [InputType.RADIO]: (input, value, indexInList) => {
      const selectedStainsForInput = getRelevantSelectedStainsForInput(input, action);
      const options = getOptions(input.inputSource, selectedStainsForInput, index, action.actionInput);

      return (
        <FormControl>
          <FormLabel id="demo-controlled-radio-buttons-group">{input.inputLabel}</FormLabel>
          <RadioGroup
            row
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={value}
            onChange={(event) => {
              const newAction = { ...action };
              if (input.parentInputKey && indexInList !== undefined) {
                const updatedParentInputValue = getUpdatedParentObject(action, input, indexInList, event.target.value);
                set(newAction, input.parentInputKey, updatedParentInputValue);
              } else {
                set(newAction, input.inputKey, event.target.value);
              }

              // check if another inputs is displayCondition on this input, if so, set it to null
              const dependentInputsOnDisplayCondition = filter(
                postProcessingActionsById[action.actionId]?.inputs,
                (inputToFind) => inputToFind.displayCondition?.key === input.inputKey
              );

              if (!isEmpty(dependentInputsOnDisplayCondition)) {
                map(dependentInputsOnDisplayCondition, (dependentInputOnDisplayCondition) => {
                  set(newAction, dependentInputOnDisplayCondition.inputKey, null);
                  if (dependentInputOnDisplayCondition?.freeSolo) {
                    set(newAction, 'newOptionValues', []);
                  }
                });
              }

              setAction(newAction, index);
            }}
          >
            {map(options, (option) => {
              return <FormControlLabel key={option.id} value={option.id} control={<Radio />} label={option.label} />;
            })}
          </RadioGroup>
        </FormControl>
      );
    },
    [InputType.LOGICAL_QUERY]: (input, value: AutomaticCondition, indexInList) => {
      const conditionOptions = [ConditionType.LOGICAL, ConditionType.ATTRIBUTES_TABLE];

      if (action.actionInput === 'area') conditionOptions.push(ConditionType.OVERLAP);

      return (
        <AutomaticConditionGroup
          conditionOptions={conditionOptions}
          editable
          onOperandsChange={(newOperands) => {
            const newValue = isEmpty(newOperands) ? null : first(newOperands);
            if (input.parentInputKey && indexInList !== undefined) {
              const updatedParentInputValue = getUpdatedParentObject(action, input, indexInList, newValue);
              setAction(set(action, input.parentInputKey, updatedParentInputValue), index);
              return;
            }
            setAction(set(action, input.inputKey, newValue), index);
          }}
          operands={value ? [value] : []}
          singleOperand
          mappingFiltersMetadata={
            mappingFiltersMetadataForLogicalQuery[input.attributesTable ?? AttributesTable.CellTable]
          }
        />
      );
    },
    [InputType.NUMBERS]: (input, value: string[], indexInList) => {
      const isError = !validateInput(get(action, input.inputKey), input, action);
      const isRequired = getIsRequired(input.required, action);

      return (
        <AutocompleteNumbers
          value={map(value, (val) => Number(val))}
          onChange={(selected) => {
            const newAction = { ...action };
            const numbers = uniq(
              map(selected, (number) => {
                return parseToFloatInRanges(number.toString(), input?.inputRange);
              })
            );
            if (input.parentInputKey && indexInList !== undefined) {
              const updatedParentInputValue = getUpdatedParentObject(action, input, indexInList, numbers);
              set(newAction, input.parentInputKey, updatedParentInputValue);
            } else {
              set(newAction, input.inputKey, isEmpty(numbers) ? null : numbers);
            }
            setAction(newAction, index);
          }}
          label={input.inputLabel}
          isError={isError}
          required={isRequired}
          inputProps={{
            max: input?.inputRange?.max,
            min: input?.inputRange?.min,
          }}
        />
      );
    },
    [InputType.BOOLEAN]: (input, value: string, indexInList) => {
      const inputLabelId = `${id}-${input.inputKey}-label`;
      return (
        <FormControl fullWidth>
          <InputLabel id={inputLabelId}>{input.inputLabel}</InputLabel>
          <Select
            labelId={inputLabelId}
            required={getIsRequired(input.required, action)}
            value={value}
            label={input.inputLabel}
            onChange={(event) => {
              const newAction = { ...action };
              if (input.parentInputKey && indexInList !== undefined) {
                const updatedParentInputValue = getUpdatedParentObject(
                  action,
                  input,
                  indexInList,
                  event.target.value === 'true' ? true : false
                );
                set(newAction, input.parentInputKey, updatedParentInputValue);
              } else {
                set(newAction, input.inputKey, event.target.value === 'true' ? true : false);
              }
              setAction(newAction, index);
            }}
          >
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
      );
    },
  };

  const getDisplayValueByOption = (input: Input, value: string) => {
    const selectedStainsForInput = getRelevantSelectedStainsForInput(input, action);
    return (
      getOptionsByValue(
        input.inputSource,
        selectedStainsForInput,
        index,
        action.actionInput,
        get(action, input.inputSourceDependentOn)
      )?.[value]?.label ?? value
    );
  };

  const getDisplayValue = (input: Input, value: string) => {
    const inputType = getInputType(input, action);
    if (inputType === InputType.LOGICAL_QUERY) {
      if (
        validateInput(
          value,
          input,
          action,
          mappingFiltersMetadataForLogicalQuery[input.attributesTable ?? AttributesTable.CellTable]
        )
      )
        return 'filters';
      else return '';
    }

    if (inputType === InputType.NUMBER) {
      return value;
    }

    if (inputType === InputType.NUMBERS) {
      return join(value, ', ');
    }

    if (inputType === InputType.BOOLEAN) {
      return value ? 'True' : 'False';
    }

    if (inputType === InputType.MULTISELECT) {
      return join(
        map(value, (valueOption) => getDisplayValueByOption(input, valueOption)),
        ', '
      );
    }

    if (inputType === InputType.SELECT || inputType === InputType.RADIO) {
      return getDisplayValueByOption(input, value);
    }

    return value;
  };

  const inputsByAdvanced = groupBy(postProcessingActionsById[action.actionId]?.inputs, 'advanced');
  const advancedInputs = inputsByAdvanced.true;
  const basicInputs = inputsByAdvanced.false;
  const instanceCountThresholdOptions =
    action.actionName === INSTANCE_COUNT_POSITIVITY_ACTION_NAME
      ? getInstanceCountThresholdOptions(
          getOptions(
            InputSource.INSTANCE_COUNT_THRESHOLDS,
            action.stain ? [action.stain] : action.stains ?? [],
            index,
            action.actionInput
          )
        )
      : [];

  const getListInput = (input: Input) => {
    const inputType = getInputType(input, action);
    if (inputType !== InputType.GROUP) {
      console.error(`Input type ${inputType} is not a list input`);
      return null;
    }

    if (isEmpty(input.children)) {
      console.error(`Input ${input.inputKey} has no children`);
      return null;
    }

    const listValues = get(action, input.inputKey, []);
    input.children = map(input.children, (childInput) => {
      childInput.parentInputKey = input.inputKey;
      return childInput;
    });

    return (
      <Grid container spacing={1}>
        {map(listValues, (listValue, indexInList) => {
          return (
            <Grid item xs={12} key={`${action.id}-${input.inputKey}-${indexInList}`}>
              <Grid container spacing={1}>
                {getInputs(input.children, indexInList)}
                <Grid item>
                  <IconButton
                    onClick={() => {
                      const newAction = { ...action };
                      const newListValues = [
                        ...slice(listValues, 0, indexInList),
                        ...slice(listValues, indexInList + 1),
                      ];
                      set(newAction, input.inputKey, newListValues);

                      // remove the option from newOptionValues array
                      const newOptionValues = cloneDeep(get(newAction, 'newOptionValues', []));
                      pullAt(newOptionValues, indexInList);
                      set(newAction, 'newOptionValues', newOptionValues);

                      setAction(newAction, index);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <IconButton
            color="primary"
            onClick={() => {
              const newListValues = [...listValues, {}];
              const newAction = { ...action };
              set(newAction, input.inputKey, newListValues);

              // add a new option - null to the newOptionValues array
              const newOptionValues = get(newAction, 'newOptionValues', []);
              newOptionValues.push(null);
              set(newAction, 'newOptionValues', newOptionValues);

              setAction(newAction, index);
            }}
            size="small"
          >
            <Typography variant="body2">{`Add ${input.inputLabel}`}</Typography>
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const getInputs = (inputs: Input[], indexInList?: number) => {
    return map(inputs, (input) => {
      const toDisplay = input.displayCondition ? getConditionValue(input.displayCondition, action) : true;
      const inputType = getInputType(input, action);
      const value = isNumber(indexInList)
        ? get(get(action, input.parentInputKey)[indexInList], input.inputKey)
        : get(action, input.inputKey, '');
      const highlightedInput = getHighlightedInput(input);
      const selectedStainsForInput = getRelevantSelectedStainsForInput(input, action);
      const canDeferPresetInput =
        Boolean(presetAuthoring) &&
        !input.freeSolo &&
        (typeof input.inputSource === 'string' || inputType === InputType.LOGICAL_QUERY);
      const presetInputOptions = canDeferPresetInput
        ? inputType === InputType.LOGICAL_QUERY
          ? mappingFiltersMetadataForLogicalQuery[input.attributesTable ?? AttributesTable.CellTable]
          : getOptions(
              input.inputSource,
              selectedStainsForInput,
              index,
              action.actionInput,
              get(action, input.inputSourceDependentOn)
            )
        : [];
      const isDeferredPresetInput = canDeferPresetInput && isEmpty(presetInputOptions);
      const hasPresetValue =
        value !== undefined && value !== null && value !== '' && (!isArray(value) || value.length > 0);
      const inputControl = isDeferredPresetInput ? (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">{input.inputLabel}</Typography>
            <Chip
              size="small"
              label={hasPresetValue ? 'Saved preset value' : 'Selected when preset is used'}
              variant="outlined"
            />
            {hasPresetValue && (
              <Typography variant="body2" color="text.secondary">
                {getDisplayValue(input, value)}
              </Typography>
            )}
            {hasPresetValue && (
              <Button
                size="small"
                onClick={() => {
                  const newAction = cloneDeep(action);
                  if (input.parentInputKey && isNumber(indexInList)) {
                    set(newAction, input.parentInputKey, getUpdatedParentObject(action, input, indexInList, undefined));
                  } else {
                    set(newAction, input.inputKey, undefined);
                  }
                  setAction(newAction, index);
                }}
              >
                Use study value instead
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {hasPresetValue
              ? 'This value is stored in the preset and applied to every matching study.'
              : 'The user selects this study-specific value when applying the preset.'}
          </Typography>
        </Box>
      ) : inputType === InputType.GROUP ? (
        getListInput(input)
      ) : (
        inputByType?.[inputType] && inputByType?.[inputType](input, value, indexInList)
      );

      return (
        toDisplay && (
          <Grid
            key={`${action.id}-${input.inputKey}`}
            item
            xs={inputType === InputType.LOGICAL_QUERY || inputType === InputType.GROUP ? 12 : 11 / basicInputs.length}
          >
            <Box
              sx={(theme) => ({
                border: highlightedInput ? 1 : 0,
                borderColor: highlightedInput ? 'primary.main' : 'transparent',
                borderRadius: theme.shape.borderRadius,
                bgcolor: highlightedInput ? 'action.selected' : 'transparent',
                p: highlightedInput ? 1.25 : 0,
              })}
            >
              {highlightedInput && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {highlightedInput.helperText}
                </Typography>
              )}
              {inputControl}
            </Box>
          </Grid>
        )
      );
    });
  };

  return (
    <Grid container spacing={1} style={style} justifyContent="flex-start">
      <Grid
        item
        sx={{
          cursor: 'grab',
        }}
        {...listeners}
        ref={setNodeRef}
        {...attributes}
        textAlign="center"
      >
        <Grid item>
          <DragHandleIcon />
        </Grid>
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            {index + 1}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={11}>
        <Accordion key={action.id} disableGutters expanded={expandAccordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => setExpandAccordion((prev) => !prev)}>
            <Grid container spacing={1}>
              <Grid item container xs={expandAccordion ? 10 : 2} alignContent="center" spacing={1}>
                <Grid item>
                  <Typography variant="h4">
                    {postProcessingActionsById[action.actionId]?.actionDisplayName ??
                      postProcessingActionsById[action.actionId]?.actionName}
                  </Typography>
                </Grid>
                <Grid item>
                  {postProcessingActionsById[action.actionId]?.deletedAt && (
                    <Typography variant="h4" color="red">
                      deprecated feature
                    </Typography>
                  )}
                </Grid>
                <Grid item>
                  <Tooltip title="Move action up">
                    <span onClick={(event) => event.stopPropagation()}>
                      <IconButton aria-label="Move action up" size="small" disabled={isFirst} onClick={moveActionUp}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <Tooltip title="Move action down">
                    <span onClick={(event) => event.stopPropagation()}>
                      <IconButton aria-label="Move action down" size="small" disabled={isLast} onClick={moveActionDown}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>

              {!expandAccordion && (
                <Grid item container xs={9}>
                  {map(postProcessingActionsById[action.actionId]?.inputs, (input) => {
                    const displayCondition = input.displayCondition
                      ? getConditionValue(input.displayCondition, action)
                      : input.advanced
                      ? Boolean(input.defaultValue != get(action, input.inputKey))
                      : true;
                    const selectedStainsForInput = getRelevantSelectedStainsForInput(input, action);
                    return (
                      displayCondition && (
                        <Grid
                          item
                          key={input.inputKey}
                          container
                          direction="column"
                          xs={size(input.inputLabel) < 10 ? 1 : 2}
                        >
                          <Grid item>
                            <Typography
                              variant="caption"
                              color={
                                !validateInput(
                                  get(action, input.inputKey),
                                  input,
                                  action,
                                  getInputType(input, action) === InputType.LOGICAL_QUERY
                                    ? mappingFiltersMetadataForLogicalQuery[
                                        input.attributesTable ?? AttributesTable.CellTable
                                      ]
                                    : getOptions(
                                        input.inputSource,
                                        selectedStainsForInput,
                                        index,
                                        action.actionInput,
                                        get(action, input.inputSourceDependentOn)
                                      )
                                )
                                  ? 'red'
                                  : ''
                              }
                            >
                              {input.inputLabel}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant="caption">
                              {getDisplayValue(input, get(action, input.inputKey))}
                            </Typography>
                          </Grid>
                        </Grid>
                      )
                    );
                  })}
                </Grid>
              )}
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container direction="column">
              {presetAuthoring && (
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Availability when this preset is used</FormLabel>
                  <RadioGroup
                    row
                    value={presetAuthoring.access}
                    onChange={(event) => presetAuthoring.onAccessChange(event.target.value as PresetActionAccess)}
                  >
                    {map(presetActionAccessOptions, (option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary">
                    {presetActionAccessDescriptions[presetAuthoring.access]}
                  </Typography>
                </FormControl>
              )}
              <Grid container spacing={1} alignItems="baseline">
                {getInputs(basicInputs)}
                <Grid item>
                  {!isEmpty(advancedInputs) && (
                    <Tooltip title="Advanced inputs">
                      <IconButton
                        onClick={() => {
                          setDisplayAdvancedInputs((prev) => !prev);
                        }}
                      >
                        {displayAdvancedInputs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>

              {displayAdvancedInputs && (
                <Grid container spacing={1} alignItems="baseline" pt={1}>
                  {getInputs(advancedInputs)}
                </Grid>
              )}

              {action.actionName === INSTANCE_COUNT_POSITIVITY_ACTION_NAME && (
                <InstanceCountPositivityThresholds
                  action={action}
                  options={instanceCountThresholdOptions}
                  onChange={(instanceName, threshold) => {
                    const nextAction = cloneDeep(action);
                    const thresholds = { ...get(nextAction, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY, {}) };
                    thresholds[instanceName] = threshold ?? null;
                    set(nextAction, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY, thresholds);
                    setAction(nextAction, index);
                  }}
                />
              )}

              {hasNonEmptyNewOptionValues(action.newOptionValues) && (
                <Grid item container justifyContent="flex-end">
                  <Typography variant="caption" color="textSecondary">
                    Pay attention- when creating a new option you won&apos;t see it in the visualization.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item pt={15}>
        <DeleteOutlineIcon
          sx={{
            cursor: 'pointer',
          }}
          onClick={() => removeAction(index)}
        />
      </Grid>
    </Grid>
  );
};

export const neighborCellClassesInputKey = 'action_inputs.neighbor_cell_classes';
export const targetCellClassesInputKey = 'action_inputs.target_cell_classes';

export const getRelevantSelectedStainsForInput = (input: Input, action: PostProcessingActionCreated) => {
  if (isFeatureBetweenStains(action)) {
    if (input.inputKey === targetCellClassesInputKey) {
      return get(action, TARGET_STAINS_INPUT_KEY, []);
    } else if (input.inputKey === neighborCellClassesInputKey) {
      return get(action, NEIGHBOR_STAINS_INPUT_KEY, []);
    }
  }
  return action?.stain
    ? [action.stain]
    : action.stains
    ? action.stains
    : concat(get(action, TARGET_STAINS_INPUT_KEY, []), get(action, NEIGHBOR_STAINS_INPUT_KEY, []));
};

const AutocompletePopper = function (props: PopperProps) {
  return <Popper {...props} style={{ width: 'fit-content', minWidth: props.style.width }} placement="bottom-start" />;
};

const getUpdatedParentObject = (
  action: PostProcessingActionCreated,
  input: Input,
  indexInList: number,
  newValue: any
) => {
  const parentInputValue = get(action, input.parentInputKey, []);
  const updatedParentInputValue = [...parentInputValue];
  updatedParentInputValue[indexInList][input.inputKey] = newValue;
  return updatedParentInputValue;
};
